import React, { useEffect, useMemo, useState } from 'react';
import { RefreshCw, Search, Trash2 } from 'lucide-react';

const PAGE_SIZE = 20;
const TYPES = [
  ['all', 'All types'],
  ['explicit_memory', 'Saved memories'],
  ['chat_fact', 'Learned facts'],
  ['chat_summary', 'Chat summaries'],
];

const sourceLabel = memory => {
  if (memory.id.startsWith('profile_')) return 'About you';
  if (memory.sourceType === 'chat_fact') return 'Learned fact';
  if (memory.sourceType === 'chat_summary') return 'Chat summary';
  return 'Saved memory';
};

const evidenceDate = memory => {
  const value = memory.sourceTime || memory.updatedAt || memory.createdAt;
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
  });
};

export default function MemoryBrowser({
  memories, isLoading, error, savedEnabled, historyEnabled, onRefresh, onDelete, deletingId,
}) {
  const [search, setSearch] = useState('');
  const [type, setType] = useState('all');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [pendingDeletion, setPendingDeletion] = useState(null);
  const available = useMemo(() => memories.filter(memory =>
    memory.sourceType === 'explicit_memory' ? savedEnabled : historyEnabled
  ).sort((a, b) =>
    (Date.parse(b.updatedAt || b.sourceTime || b.createdAt) || 0) -
    (Date.parse(a.updatedAt || a.sourceTime || a.createdAt) || 0)
  ), [memories, savedEnabled, historyEnabled]);
  const filtered = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    return available.filter(memory =>
      (type === 'all' || memory.sourceType === type) &&
      (!query || `${memory.text} ${memory.sourceTitle || ''}`.toLocaleLowerCase().includes(query))
    );
  }, [available, search, type]);
  useEffect(() => { setVisibleCount(PAGE_SIZE); }, [search, type]);

  return (
    <section className="memory-browser settings-group" aria-labelledby="memory-browser-heading">
      <div className="memory-browser-heading">
        <div>
          <h3 id="memory-browser-heading">All memories</h3>
          <p className="settings-note">Saved details, learned facts, and summaries from your past chats.</p>
        </div>
        <button type="button" className="settings-button" onClick={onRefresh} disabled={isLoading || !!deletingId}>
          <RefreshCw size={15} aria-hidden="true" />Refresh
        </button>
      </div>

      <div className="memory-browser-controls">
        <label className="memory-browser-search">
          <Search size={17} aria-hidden="true" />
          <input type="search" aria-label="Search memories" placeholder="Search memories"
            value={search} onChange={event => setSearch(event.target.value)} />
        </label>
        <label className="memory-browser-filter">
          <span className="sr-only">Memory type</span>
          <select value={type} onChange={event => setType(event.target.value)}>
            {TYPES.map(([value, label]) => <option value={value} key={value}>{label}</option>)}
          </select>
        </label>
      </div>

      {error && <p role="alert" className="memory-browser-error">{error}</p>}
      <p className="settings-muted memory-browser-count" role="status">
        {isLoading ? 'Loading memories…' : `${filtered.length} ${filtered.length === 1 ? 'memory' : 'memories'}${search || type !== 'all' ? ` matching · ${available.length} total` : ''}`}
      </p>

      {!isLoading && !error && available.length === 0 ? (
        <div className="memory-browser-empty">
          <p>{savedEnabled || historyEnabled ? 'No memories yet.' : 'Memory is turned off.'}</p>
          <p className="settings-note">{savedEnabled || historyEnabled
            ? 'Save a detail above or let Arrow learn from your chats. New memories will appear here.'
            : 'Enable saved memories or chat history above to view memories.'}</p>
        </div>
      ) : !isLoading && available.length > 0 && filtered.length === 0 ? (
        <div className="memory-browser-empty">
          <p>No matching memories.</p>
          <button type="button" className="settings-button" onClick={() => { setSearch(''); setType('all'); }}>
            Clear filters
          </button>
        </div>
      ) : (
        <ul className="memory-browser-list" aria-busy={isLoading}>
          {filtered.slice(0, visibleCount).map(memory => (
            <li key={memory.id} className="memory-browser-item">
              <div className="memory-browser-item-content">
                <div className="memory-browser-meta">
                  <span className="memory-browser-kind">{sourceLabel(memory)}</span>
                  {evidenceDate(memory) && <span>{evidenceDate(memory)}</span>}
                </div>
                <p className="memory-browser-text">{memory.text}</p>
                {memory.sourceTitle && <p className="settings-note memory-browser-source">From: {memory.sourceTitle}</p>}
                {pendingDeletion === memory.id && (
                  <div className="memory-browser-confirm" role="group" aria-label="Confirm memory deletion">
                    <p>Delete this memory? {memory.chatId
                      ? 'Arrow will also stop using other memories from this chat. The chat itself will stay.'
                      : 'Arrow will stop using this detail. This cannot be undone.'}</p>
                    <div className="settings-actions">
                      <button type="button" className="settings-button" disabled={!!deletingId}
                        onClick={() => setPendingDeletion(null)}>Cancel</button>
                      <button type="button" className="settings-button settings-danger" disabled={!!deletingId}
                        onClick={async () => {
                          if (await onDelete(memory.id)) setPendingDeletion(null);
                        }}>{deletingId === memory.id ? 'Deleting…' : 'Delete memory'}</button>
                    </div>
                  </div>
                )}
              </div>
              <button type="button" className="memory-browser-delete" disabled={!!deletingId}
                onClick={() => setPendingDeletion(memory.id)} title="Delete memory"
                aria-label={`Delete memory: ${memory.text}`}>
                <Trash2 size={17} aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {filtered.length > visibleCount && (
        <button type="button" className="settings-button memory-browser-more"
          onClick={() => setVisibleCount(count => count + PAGE_SIZE)}>
          Show more ({filtered.length - visibleCount} remaining)
        </button>
      )}
      {available.some(memory => memory.sourceType !== 'explicit_memory') && (
        <p className="settings-note">Learned facts and summaries may be incomplete or out of date. You can delete anything you don’t want Arrow to use.</p>
      )}
    </section>
  );
}
